import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Ban, Clock, CheckCircle2, Eye, Plus, RefreshCw, Link2 } from "lucide-react";

const STATUS_COLORS = {
  PENDING:   { bg: "rgba(250,204,21,0.12)", fg: "#facc15",   label: "Pendiente" },
  OPENED:    { bg: "rgba(59,130,246,0.14)", fg: "#60a5fa",   label: "Abierto" },
  COMPLETED: { bg: "rgba(34,197,94,0.14)",  fg: "#4ade80",   label: "Completado" },
  REVIEWED:  { bg: "rgba(147,51,234,0.14)", fg: "#c084fc",   label: "Revisado" },
  EXPIRED:   { bg: "rgba(115,115,115,0.14)", fg: "#a3a3a3",  label: "Expirado" },
  REVOKED:   { bg: "rgba(239,68,68,0.14)",  fg: "#f87171",   label: "Revocado" },
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.PENDING;
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "3px 10px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", borderRadius: 999, fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

export default function AdminDiagnostics() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ player_name: "", ttl_days: 14, notes: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/diagnostics/invitations");
      setItems(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createInvitation = async () => {
    if (!form.player_name.trim()) return toast.error("Nombre del jugador requerido");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/diagnostics/invitations", form);
      const url = `${window.location.origin}/diagnostico/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Invitación creada y enlace copiado al portapapeles");
      } catch {
        toast.success("Invitación creada");
      }
      setShowNew(false);
      setForm({ player_name: "", ttl_days: 14, notes: "" });
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/diagnostico/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta invitación? El enlace dejará de funcionar.")) return;
    try {
      await api.post(`/admin/diagnostics/invitations/${id}/revoke`);
      toast.success("Invitación revocada");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const shareWhatsApp = (token, playerName) => {
    const url = `${window.location.origin}/diagnostico/${token}`;
    const text = `Hola! Aquí tienes el diagnóstico inicial de QV Performance${playerName ? ` para ${playerName}` : ""}. Tarda unos 5 minutos: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const counts = items.reduce((a, i) => { a[i.status] = (a[i.status] || 0) + 1; return a; }, {});

  return (
    <AppShell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Diagnósticos</h1>
          <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 0" }}>Invita a un padre a completar el cuestionario inicial. Nunca autentica; sólo permite ese diagnóstico.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} data-testid="diag-refresh-btn" style={btnGhost}><RefreshCw size={14} /> Actualizar</button>
          <button onClick={() => setShowNew(true)} data-testid="diag-new-btn" style={btnPrimary}><Plus size={14} /> Nuevo diagnóstico</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 24 }}>
        {["PENDING","OPENED","COMPLETED","REVIEWED","EXPIRED","REVOKED"].map((k) => (
          <div key={k} style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: "14px 16px", borderRadius: 4 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3" }}>{STATUS_COLORS[k].label}</div>
            <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 32, color: STATUS_COLORS[k].fg, lineHeight: 1 }}>{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      {showNew && (
        <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", padding: 20, borderRadius: 4, marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, letterSpacing: "0.14em", textTransform: "uppercase" }}>Nueva invitación</h3>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Nombre del jugador *</label>
              <input value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} placeholder="Ej. Rodrigo López" style={inputStyle} data-testid="diag-form-player-name" />
            </div>
            <div>
              <label style={labelStyle}>Caducidad (días)</label>
              <input type="number" min={1} max={90} value={form.ttl_days} onChange={(e) => setForm({ ...form, ttl_days: parseInt(e.target.value) || 14 })} style={inputStyle} data-testid="diag-form-ttl" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Nota interna (opcional)</label>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contacto, referido por..." style={inputStyle} data-testid="diag-form-notes" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={createInvitation} disabled={creating} style={btnPrimary} data-testid="diag-form-submit">
              {creating ? "Creando…" : "Crear y copiar enlace"}
            </button>
            <button onClick={() => setShowNew(false)} style={btnGhost}>Cancelar</button>
          </div>
        </div>
      )}

      <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>
        {loading && <div style={{ padding: 24, textAlign: "center", color: "#a3a3a3" }}>Cargando…</div>}
        {!loading && items.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#a3a3a3" }}>Aún no hay invitaciones. Crea la primera.</div>}
        {!loading && items.map((i) => (
          <div key={i.id} data-testid={`diag-item-${i.id}`} style={{ padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", minWidth: 0 }}>
              <StatusPill status={i.status} />
              <div>
                <div style={{ fontWeight: 600 }}>{i.player_name || "(sin nombre)"}</div>
                <div style={{ fontSize: 11, color: "#a3a3a3", letterSpacing: "0.05em", marginTop: 2 }}>
                  Creado {new Date(i.created_at).toLocaleDateString()} · Caduca {new Date(i.expires_at).toLocaleDateString()}
                  {i.submitted_at && ` · Enviado ${new Date(i.submitted_at).toLocaleDateString()}`}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {i.status === "COMPLETED" && i.submission_id && (
                <Link to={`/app/admin/diagnosticos/${i.submission_id}`} style={btnSmallPrimary} data-testid={`diag-review-${i.id}`}>
                  <Eye size={13} /> Revisar
                </Link>
              )}
              {(i.status === "PENDING" || i.status === "OPENED") && (
                <>
                  <button onClick={() => copyLink(i.token)} style={btnSmall} data-testid={`diag-copy-${i.id}`}><Copy size={13} /> Copiar</button>
                  <button onClick={() => shareWhatsApp(i.token, i.player_name)} style={btnSmall}><Link2 size={13} /> WhatsApp</button>
                  <button onClick={() => revoke(i.id)} style={{ ...btnSmall, color: "#f87171" }} data-testid={`diag-revoke-${i.id}`}><Ban size={13} /> Revocar</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}

const inputStyle = { width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "9px 12px", fontSize: 14, borderRadius: 3, outline: "none", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 5 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, background: "#dc2626", color: "#fff", border: "none", padding: "9px 16px", fontSize: 13, letterSpacing: "0.06em", fontWeight: 600, cursor: "pointer", borderRadius: 2, textDecoration: "none" };
const btnGhost = { display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "#e5e5e5", border: "1px solid rgba(255,255,255,0.14)", padding: "9px 14px", fontSize: 13, cursor: "pointer", borderRadius: 2 };
const btnSmall = { display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", color: "#e5e5e5", border: "1px solid rgba(255,255,255,0.14)", padding: "6px 10px", fontSize: 12, cursor: "pointer", borderRadius: 2 };
const btnSmallPrimary = { display: "inline-flex", alignItems: "center", gap: 5, background: "#dc2626", color: "#fff", border: "none", padding: "6px 12px", fontSize: 12, cursor: "pointer", borderRadius: 2, textDecoration: "none", fontWeight: 600 };
